'use client';

import { useMemo } from 'react';
import { useBooking } from '../../context/BookingContext';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import { computeCompatiblePlaceIds } from '../../utils/place-compat';
import { groupPlacesByKind, type PlaceGroup, type PlaceGroupKey } from '../../utils/place-groups';
import { PlaceCard } from '../shared/PlaceCard';

export function PlaceSelection() {
  const { ctx, send, labels, goBack } = useBooking();

  /* Étape à auto-avance (le clic sur un lieu confirme) : pas d'action principale. */
  useRegisterStepNav(useMemo((): StepNav => ({ onBack: goBack }), [goBack]));

  const compatiblePlaceIds = useMemo(
    (): Set<number> =>
      computeCompatiblePlaceIds(
        ctx.cartItems,
        ctx.availablePlaces.map((p): number => p.pro_place_id),
      ),
    [ctx.cartItems, ctx.availablePlaces],
  );

  const noneCompatible =
    ctx.availablePlaces.length > 0 &&
    ctx.availablePlaces.every((p): boolean => !compatiblePlaceIds.has(p.pro_place_id));

  /* En établissement / À distance / À domicile (bug 36) ; un seul groupe : pas de titre. */
  const groups = useMemo((): PlaceGroup[] => groupPlacesByKind(ctx.availablePlaces), [ctx.availablePlaces]);
  const showGroupTitles = groups.length > 1;
  const groupTitle: Record<PlaceGroupKey, string> = {
    establishment: labels.place_group_establishment,
    remote: labels.place_group_remote,
    traveling: labels.place_group_traveling,
  };

  return (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.choose_place}</h2>
      </div>

      {noneCompatible && <div className="bt-banner bt-banner--warning">{labels.place_none_for_services}</div>}

      <div className="bt-place-list">
        {groups.map((group) => (
          <section
            key={group.key}
            className="bt-place-group"
            aria-labelledby={showGroupTitles ? `bt-place-group-${group.key}` : undefined}
          >
            {showGroupTitles && (
              <h3 id={`bt-place-group-${group.key}`} className="bt-place-group__title">
                {groupTitle[group.key]}
              </h3>
            )}
            {group.places.map((place) => {
              const disabled = !compatiblePlaceIds.has(place.pro_place_id);
              return (
                <PlaceCard
                  key={place.pro_place_id}
                  place={place}
                  isSelected={ctx.selectedPlaceId === place.pro_place_id}
                  disabled={disabled}
                  disabledReason={labels.place_unavailable_for_services}
                  onClick={() => send({ type: 'SELECT_PLACE', placeId: place.pro_place_id })}
                />
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
}
